import React, { useMemo } from "react";
import { Marker, Popup, Tooltip as MapTooltip } from "react-leaflet";
import L from "leaflet";
import { Box, Button, GlobalStyles, Typography } from "@mui/material";
import { MAX_RADIUS, pieSvg } from "./mapModel";

const NUMBER = new Intl.NumberFormat();

// A count that opens the specimens behind it.
export const CountButton = ({ count, label, onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    aria-label={`${label}: ${NUMBER.format(count)}. Show these specimens`}
    sx={{
      font: "inherit",
      color: "primary.main",
      p: 0,
      border: 0,
      bgcolor: "transparent",
      cursor: "pointer",
      fontVariantNumeric: "tabular-nums",
      "&:hover": { textDecoration: "underline" },
    }}
  >
    {NUMBER.format(count)}
  </Box>
);

export const Swatch = ({ color }) => (
  <Box
    component="span"
    aria-hidden
    sx={{
      width: 10,
      height: 10,
      borderRadius: 0.5,
      bgcolor: color,
      flexShrink: 0,
    }}
  />
);

const SitePopup = ({ pie, onOpen }) => (
  <Box
    sx={{
      minWidth: 220,
      maxWidth: 320,
      fontFamily: (t) => t.typography.fontFamily,
      fontSize: "0.8125rem",
    }}
  >
    <Typography
      component="h3"
      sx={{ fontWeight: 500, fontSize: "0.9375rem", lineHeight: 1.3 }}
    >
      {pie.name}
    </Typography>
    {/* not a <p>: Leaflet gives paragraphs in a popup wide margins */}
    <Typography
      variant="caption"
      color="text.secondary"
      component="div"
      sx={{ mb: 1 }}
    >
      {[pie.county, `${NUMBER.format(pie.total)} specimens`]
        .filter(Boolean)
        .join(" · ")}
    </Typography>
    {/* a site with many species scrolls its list; the name and Show all stay put */}
    <Box
      sx={{ maxHeight: 240, overflowY: "auto", pr: 0.5 }}
      data-testid="site-species"
    >
      <Box component="table" sx={{ borderCollapse: "collapse", width: "100%" }}>
        <Box component="tbody">
          {pie.species.map((s) => (
            <Box component="tr" key={s.label}>
              <Box component="td" sx={{ py: 0.25, pr: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Swatch color={s.color} />
                  {s.label}
                </Box>
              </Box>
              <Box component="td" sx={{ py: 0.25, textAlign: "right" }}>
                <CountButton
                  count={s.count}
                  label={`${s.label}, ${pie.name}`}
                  onClick={() => onOpen({ row: pie.row, column: s.column })}
                />
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
    <Button
      size="small"
      sx={{ mt: 1, ml: -0.75 }}
      onClick={() => onOpen({ row: pie.row })}
    >
      Show all {NUMBER.format(pie.total)} specimens
    </Button>
  </Box>
);

/**
 * Each located collection site as a pie of its species (mapModel), with its
 * name and total on hover and its species in a popup. Must sit inside a
 * react-leaflet MapContainer. `onOpen({row, column?})` opens the specimens
 * behind a count.
 */
export const SitePies = ({ pies, onOpen }) => {
  const icons = useMemo(
    () =>
      new Map(
        pies.map((pie) => {
          const size = Math.ceil((pie.radius + 2) * 2);
          return [
            pie.key,
            L.divIcon({
              html: pieSvg(pie),
              className: "report-site-pie",
              iconSize: [size, size],
              iconAnchor: [size / 2, size / 2],
            }),
          ];
        }),
      ),
    [pies],
  );

  return (
    <>
      {/* the pies are their own drawing: no default divIcon box around them */}
      <GlobalStyles
        styles={(t) => ({
          ".report-site-pie": { background: "none", border: 0 },
          ".report-site-pie svg": { display: "block" },
          ".report-site-pie:focus-visible": {
            outline: `2px solid ${t.palette.primary.main}`,
            borderRadius: "50%",
          },
        })}
      />
      {pies.map((pie) => (
        <Marker
          key={pie.key}
          position={pie.position}
          icon={icons.get(pie.key)}
          // smaller sites draw over larger ones
          zIndexOffset={Math.round((MAX_RADIUS - pie.radius) * 100)}
          keyboard
        >
          <MapTooltip direction="top" offset={[0, -pie.radius]} opacity={1}>
            {pie.name} · {NUMBER.format(pie.total)}
          </MapTooltip>
          <Popup maxWidth={340} minWidth={220} autoPanPadding={[24, 24]}>
            <SitePopup pie={pie} onOpen={onOpen} />
          </Popup>
        </Marker>
      ))}
    </>
  );
};
